import type { ReactNode } from "react";
import { ApprovalFlowPanel } from "@/components/workflow/ApprovalFlowSidebar";
import type { ILeaveApprovalSteps } from "@/types/(my-space)/leaves.interface";
import type { IAssetApprovalSteps } from "@/types/(company)/assets.interface";
import type { IApprovalSteps } from "@/types/(company)/workflow.interface";
import type { SidebarTab } from "../RequestDetailSidebar";

/** All approval steps shapes accepted by ApprovalFlowPanel. */
type ApprovalSteps = ILeaveApprovalSteps | IAssetApprovalSteps | IApprovalSteps;

interface ApprovalFlowTabOptions {
  /** approvalSteps from workflowData?.data?.approvalSteps. */
  approvalSteps?: ApprovalSteps | null;
  isLoading?: boolean;
  /** Module-specific trigger content rendered above the step list. */
  triggerContent?: ReactNode;
  /** Whether to show the progress bar. Default true. */
  showProgress?: boolean;
  /** Tab label. Default "APPROVAL FLOW". */
  label?: string;
  /** Tab value key. Default "approval-steps". */
  value?: string;
}

/**
 * Factory function returning a SidebarTab config for the approval flow.
 *
 * Eliminates the 15 duplicated inline ApprovalFlowPanel wirings across
 * all request detail pages.
 *
 * @example
 * sidebarTabs={[
 *   approvalFlowTab({
 *     approvalSteps: workflowData?.data?.approvalSteps,
 *     isLoading: isWorkflowLoading || isWorkflowFetching,
 *     triggerContent: <LeaveInfo .../>,
 *   }),
 * ]}
 */
export function approvalFlowTab({
  approvalSteps,
  isLoading,
  triggerContent,
  showProgress = true,
  label = "my-space:ui.approval_flow",
  value = "approval-steps",
}: ApprovalFlowTabOptions = {}): SidebarTab {
  return {
    value,
    label,
    content: (
      <ApprovalFlowPanel
        approvalSteps={approvalSteps ?? undefined}
        isLoading={isLoading}
        triggerContent={triggerContent}
        showProgress={showProgress}
      />
    ),
  };
}
